import React from "react";
import { Link, useLocation } from "react-router-dom";
import { SUPPORT_EMAIL } from "../SEO/siteOrigin";
import { PRODUCT_MENU_GROUPS } from "../../utility/marketingProductNav";

const legalLinks = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-service", label: "Terms of Service" },
  { to: "/disclaimer", label: "Disclaimer" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/data-retention-policy", label: "Data Retention Policy" },
  { to: "/accessibility-statement", label: "Accessibility" },
];

const socialLinks = [
  {
    url: "https://www.facebook.com/people/Bowling-Tournament-App/61576605428527/",
    ariaLabel: "Bowling Tournament App on Facebook",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    url: "https://www.reddit.com/user/BowlingTournamentApp",
    ariaLabel: "Bowling Tournament App on Reddit",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
      </svg>
    ),
  },
  {
    url: "https://x.com/RunTheSquad",
    ariaLabel: "Bowling Tournament App on X",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    url: "https://www.youtube.com/@BowlingTournamentApp",
    ariaLabel: "Bowling Tournament App on YouTube",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
];

const linkClass = "hover:text-brand-gold transition-colors";

const FooterSection = ({ id, title, children }) => (
  <nav aria-labelledby={id} className="min-w-0">
    <h2 id={id} className="mb-2 text-sm font-semibold text-brand-cream">
      {title}
    </h2>
    <ul className="flex flex-col gap-1 text-sm text-brand-cream/80">{children}</ul>
  </nav>
);

const Footer = () => {
  const { pathname } = useLocation();
  const onShellPage =
    pathname.startsWith("/tournament/") || pathname.startsWith("/organization");

  return (
    <footer
      className={`bg-brand-charcoal text-brand-cream py-6 mt-auto ${
        onShellPage
          ? "max-lg:pb-[calc(1.5rem+var(--tournament-mobile-clearance,3.5rem))]"
          : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <FooterSection id="footer-product-heading" title="Product">
            <li>
              <Link to="/bowling-tournament-software" className={linkClass}>
                Bowling tournament software
              </Link>
            </li>
            <li>
              <Link to="/pricing" className={linkClass}>
                Plans
              </Link>
            </li>
            {PRODUCT_MENU_GROUPS.map((group) => (
              <li key={group.id} className="pt-2">
                {group.to ? (
                  <Link
                    to={group.to}
                    className="mb-1 inline-block text-xs font-semibold uppercase tracking-wide text-brand-cream/80 hover:text-brand-gold"
                  >
                    {group.label}
                  </Link>
                ) : (
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-cream/60 mb-1">
                    {group.label}
                  </p>
                )}
                <ul className="flex flex-col gap-1">
                  {group.items.map((item) => (
                    <React.Fragment key={item.to}>
                      <li>
                        <Link to={item.to} className={linkClass}>
                          {item.label}
                        </Link>
                      </li>
                      {(item.children || []).map((child) => (
                        <li key={child.to} className="pl-3">
                          <Link to={child.to} className={linkClass}>
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </React.Fragment>
                  ))}
                </ul>
              </li>
            ))}
          </FooterSection>

          <FooterSection id="footer-support-heading" title="Support">
            <li>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={`${linkClass} break-words`}
              >
                {SUPPORT_EMAIL}
              </a>
            </li>
            <li>
              <Link to="/support" className={linkClass}>
                Support
              </Link>
            </li>
          </FooterSection>

          <FooterSection id="footer-legal-heading" title="Legal">
            {legalLinks.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className={linkClass}>
                  {link.label}
                </Link>
              </li>
            ))}
          </FooterSection>
        </div>

        <div className="mt-6">
          <p className="text-sm text-brand-cream/80 mb-2">Follow us</p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.url}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.ariaLabel}
                className="text-brand-cream/80 hover:text-brand-gold transition-colors"
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-brand-line text-xs">
          <div className="flex items-baseline justify-between gap-x-6">
            <p className="min-w-0 text-brand-cream/70">
              &copy; {new Date().getFullYear()} Bowling Tournament App. All rights
              reserved.
            </p>
            <p className="shrink-0 text-right text-[11px] leading-4 font-normal text-brand-mist/80">
              Aim for the big white globs - Andre C
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
